import {
  createEmptySectionHeadingMarker,
  createSectionElementRegex,
  DRAFT_SECTION_SENTINEL,
  isEmptySectionHeadingContent,
  isSectionEndElement,
  SECTION_END_MARKER_HTML,
  stripDraftSectionSentinel,
} from '@heidi/editor/core';

/**
 * Section parser utilities for wrapping/unwrapping HTML in `<section>` elements.
 *
 * Used in the markdown editor pipeline:
 *   Inbound:  markdown → simpleMarkdownToHtml() → wrapHtmlInSections() → TipTap
 *   Outbound: TipTap → editor.getHTML() → stripSectionWrappers() → simpleHtmlToMarkdown()
 */

/**
 * Regex to match `<p># heading text</p>` produced by simpleMarkdownToHtml.
 * Only matches single `#` (not `##`).
 */
const HEADING_PATTERN = /^<p>#(?: (.*?))?<\/p>$/;

/**
 * Counts the top-level sections in a markdown template — a section opens at each
 * single-`#` ATX H1 line, the same rule `wrapHtmlInSections` applies (`##`+ are
 * content headings, not sections). Used for the `section_count` analytics
 * dimension on template create/update. This is an analytics estimate: a `# ` line
 * inside a fenced code block is not excluded (negligible for templates).
 */
export const countMarkdownSections = (markdown: string | null | undefined): number =>
  markdown ? (markdown.match(/^ {0,3}#(?: |(?=\r?$))/gm) ?? []).length : 0;

export const escapeSectionTitle = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Post-processes HTML from `simpleMarkdownToHtml()`.
 * Detects `<p># heading text</p>` patterns and groups content between headings
 * into `<section data-section-title="heading text">...</section>` wrappers.
 *
 * - Content before the first heading (preamble) is left unwrapped.
 * - If no `# ` headings exist, returns input unchanged.
 * - Only single `#` is matched — `##`, `###` etc. are not treated as sections.
 */
export function wrapHtmlInSections(html: string): string {
  // Split HTML into top-level elements by finding all tags.
  // simpleMarkdownToHtml produces a flat sequence of <p>, <hr>, <table> tags.
  const elements = splitHtmlElements(html);
  const result: string[] = [];
  let hasHeading = false;
  let inSection = false;

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i] ?? '';

    // An explicit section-close marker ends the current section so the blocks
    // after it stay top-level (they were typed outside the section). Drop the
    // marker itself — it never becomes editor content.
    if (isSectionEndElement(element)) {
      if (inSection) {
        result.push('</section>');
        inSection = false;
      }
      continue;
    }

    const match = element.match(HEADING_PATTERN);

    if (match) {
      hasHeading = true;
      if (inSection) {
        result.push('</section>');
      }

      const rawHeadingContent = match[1] ?? '';
      const headingIsEmpty = isEmptySectionHeadingContent(rawHeadingContent);
      const headingContent = headingIsEmpty
        ? DRAFT_SECTION_SENTINEL
        : stripDraftSectionSentinel(rawHeadingContent);
      const plainTitle = headingIsEmpty
        ? 'New Section'
        : headingContent.replace(/<[^>]*>/g, '');
      const escapedTitle = escapeSectionTitle(plainTitle);
      result.push(`<section data-section-title="${escapedTitle}">`);
      inSection = true;
      result.push(`<h1>${headingContent}</h1>`);
      continue;
    }

    result.push(element);
  }

  if (!hasHeading) {
    return html;
  }

  if (inSection) {
    result.push('</section>');
  }

  return result.join('');
}

/**
 * Removes `<section ...>` and `</section>` tags from HTML, preserving inner content.
 * Handles attribute values that may contain `>` by matching quoted strings.
 * Used before converting HTML back to markdown so the DB never sees section wrappers.
 */
export function stripSectionWrappers(
  html: string,
  {
    emitEmptySectionHeadingMarker,
    emitSectionEndMarkers = false,
  }: {
    /**
     * `true` emits a numbered persistence marker, `false` omits the empty
     * heading for presentation output, and omission preserves the legacy
     * sentinel-bearing `# ${DRAFT_SECTION_SENTINEL}` boundary.
     */
    emitEmptySectionHeadingMarker?: boolean;
    emitSectionEndMarkers?: boolean;
  } = {}
): string {
  let emptyHeadingMarkerIdentity = 0;
  const htmlWithSectionHeadingsConverted = html.replace(
    createSectionElementRegex(),
    (match: string, _attrs: string, sectionContent: string, offset: number) => {
      const inner = sectionContent.replace(
        /<h1>([\s\S]*?)<\/h1>/gi,
        (_heading, rawHeadingContent: string) => {
          if (isEmptySectionHeadingContent(rawHeadingContent)) {
            if (emitEmptySectionHeadingMarker === true) {
              return `<p># ${createEmptySectionHeadingMarker(
                ++emptyHeadingMarkerIdentity
              )}</p>`;
            }
            if (emitEmptySectionHeadingMarker === false) return '';
            return `<p># ${rawHeadingContent}</p>`;
          }
          return `<p># ${stripDraftSectionSentinel(rawHeadingContent)}</p>`;
        }
      );
      if (!emitSectionEndMarkers) return inner;
      // On the persistence path only, emit a section-close marker when a
      // non-section block follows, so text outside the section survives the
      // markdown round trip instead of being re-absorbed on reload. Adjacent
      // sections (or EOF) need no marker. Off for copy/export/EHR paths so the
      // marker never leaks into user-facing output.
      const rest = html.slice(offset + match.length);
      const followedByNonSection = rest.trim() !== '' && !/^\s*<section\b/i.test(rest);
      return inner + (followedByNonSection ? SECTION_END_MARKER_HTML : '');
    }
  );

  return htmlWithSectionHeadingsConverted
    .replace(/<section(?:\s+[a-z-]+="[^"]*")*\s*>/gi, '')
    .replace(/<\/section>/gi, '');
}

/**
 * Splits a flat HTML string into an array of top-level element strings.
 * Handles self-closing tags like `<hr>` and block-level tags like `<p>`, `<table>`.
 */
function splitHtmlElements(html: string): string[] {
  const elements: string[] = [];
  // HTML tag names must start with a letter (HTML5 spec). Anchoring with
  // `[a-zA-Z]` prevents stray content like "<6.5%)" inside list items —
  // common in lab-result narrative ("normal <6.5%") — from being mis-parsed
  // as an opening tag, which would corrupt depth tracking and drop every
  // subsequent top-level element from the section-wrapping pass.
  // Also matches HTML comments so a top-level section-close marker surfaces as
  // its own element (the tag alternative's capture groups stay undefined for a
  // comment match, so the tag branch below is skipped for it).
  const tagRegex = /<!--[\s\S]*?-->|<(\/?)([a-zA-Z][\w-]*)([^>]*)>/g;
  let depth = 0;
  let currentTag = '';
  let elementStart = 0;
  let match;

  while ((match = tagRegex.exec(html)) !== null) {
    if (match[0].startsWith('<!--')) {
      if (depth === 0) elements.push(match[0]);
      continue;
    }
    const isClosing = match[1] === '/';
    const tagName = match[2] ?? '';
    const isSelfClosing =
      tagName === 'hr' || tagName === 'br' || (match[3] ?? '').endsWith('/');

    if (!isClosing && !isSelfClosing) {
      if (depth === 0) {
        elementStart = match.index;
        currentTag = tagName;
      }
      depth++;
    } else if (isSelfClosing && depth === 0) {
      elements.push(html.slice(match.index, match.index + match[0].length));
    } else if (isClosing && depth > 0) {
      depth--;
      if (depth === 0 && tagName === currentTag) {
        elements.push(html.slice(elementStart, match.index + match[0].length));
      }
    }
  }

  return elements;
}
